import React from 'react';
import { X, AlertCircle, Coins, Loader2 } from 'lucide-react';

/**
 * Reusable Confirm-and-Charge dialog.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onConfirm: () => Promise<void>
 *  - title: string
 *  - description: string|node
 *  - cost: number                  // MP to charge
 *  - balance: number               // current balance (for the gate)
 *  - allowNegative?: boolean       // if true, no balance gate
 *  - confirmLabel?: string
 *  - lineItems?: [{label, amount}] // optional breakdown
 */
export default function ConfirmChargeDialog({
  open, onClose, onConfirm, title, description, cost, balance,
  allowNegative = false, confirmLabel = 'Confirm', lineItems = [],
}) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => { if (open) { setError(null); setBusy(false); } }, [open]);

  if (!open) return null;
  const insufficient = !allowNegative && balance < cost;
  const after = balance - cost;

  const handle = async () => {
    setError(null);
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="confirm-charge-dialog">
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center"><Coins className="w-5 h-5 text-amber-600 dark:text-amber-400" /></div>
            <div>
              <h2 className="text-slate-900 dark:text-white font-semibold text-base">{title}</h2>
              <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">Confirm Mu Points charge</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid="close-confirm-charge"><X className="w-4 h-4" /></button>
        </div>

        <div className="text-slate-600 dark:text-zinc-300 text-sm mb-4">{description}</div>

        {lineItems.length > 0 && (
          <div className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg p-3 text-xs space-y-1.5 mb-3" data-testid="charge-line-items">
            {lineItems.map((li, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-slate-500 dark:text-zinc-400">{li.label}</span>
                <span className="text-slate-700 dark:text-zinc-200">{li.amount} MP</span>
              </div>
            ))}
          </div>
        )}

        <div className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg p-3 text-xs space-y-1.5 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 dark:text-zinc-400">Total charge</span>
            <span className="text-amber-700 dark:text-amber-300 font-semibold">{cost} MP</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-500 dark:text-zinc-400">Current balance</span>
            <span className="text-slate-700 dark:text-zinc-200">{balance} MP</span>
          </div>
          <div className="flex items-center justify-between border-t border-slate-200 dark:border-zinc-800 pt-1.5">
            <span className="text-slate-500 dark:text-zinc-400">Balance after</span>
            <span className={after < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-emerald-700 dark:text-emerald-300 font-semibold'} data-testid="balance-after">{after} MP</span>
          </div>
        </div>

        {insufficient && (
          <div className="mb-3 px-3 py-2 rounded bg-red-500/10 text-red-600 dark:text-red-400 text-xs flex items-start gap-2" data-testid="insufficient-balance">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Insufficient balance — top up Mu Points to continue.</span>
          </div>
        )}
        {allowNegative && after < 0 && (
          <div className="mb-3 px-3 py-2 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Auto-mode allows negative balance — your wallet will go to {after} MP. Top up later from the Wallet page.</span>
          </div>
        )}

        {error && <div className="mb-3 px-3 py-2 rounded bg-red-500/10 text-red-600 dark:text-red-400 text-xs">{error}</div>}

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm hover:bg-slate-200 dark:hover:bg-zinc-700" data-testid="cancel-charge-btn">Cancel</button>
          <button onClick={handle} disabled={insufficient || busy} className="flex-1 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-charge-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
            {busy ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
